import React from 'react';
import { Button } from '@/components/ui/button';
import { MessageCircle, Clock, Target, Users } from 'lucide-react';

interface QuickPromptsProps {
  artifactType: 'big-sheet' | 'power-moves' | 'okrs' | 'opportunity';
  sectionType?: string;
  onPromptSelect: (prompt: string) => void;
}

export const QuickPrompts: React.FC<QuickPromptsProps> = ({
  artifactType,
  sectionType,
  onPromptSelect
}) => {
  const getPrompts = (): string[] => {
    const prompts = {
      'big-sheet': {
        'support-system': [
          "Who has opened doors for you recently?",
          "Which community or group helps your career?", 
          "Who advocates for you at work?",
          "Name a mentor who shaped your path"
        ],
        'skills-inventory': [
          "What's your strongest professional skill?",
          "Where do you feel less confident?",
          "What skill would get you promoted?",
          "Which ability sets you apart?"
        ],
        'career-vision': [
          "Where do you want to be in 2 years?",
          "What type of work energizes you?",
          "What impact do you want to make?",
          "What would success look like?"
        ],
        'achievements': [
          "What's your biggest career win?",
          "Which project had the most impact?",
          "What result are you most proud of?",
          "When did you exceed expectations?"
        ],
        'values': [
          "What matters most in your work?",
          "Which company value resonates with you?",
          "What motivates you daily?",
          "What won't you compromise on?"
        ],
        'default': [
          "What's most important here?",
          "How does this impact your career?",
          "What would success look like?",
          "What should you focus on?"
        ]
      },
      'power-moves': {
        'strategic': [
          "What project would showcase your skills?",
          "How can you increase your visibility?",
          "What relationship could open doors?",
          "Which skill would make you invaluable?"
        ],
        'networking': [
          "Who should you meet in your industry?",
          "What event could expand your network?",
          "How can you help others first?",
          "Which introduction would be game-changing?"
        ],
        'skill-building': [
          "What skill would accelerate your career?",
          "Which certification would help most?",
          "What learning would pay dividends?",
          "Which expertise is in high demand?"
        ],
        'visibility': [
          "How can you share your expertise?",
          "What platform could showcase your work?",
          "Which speaking opportunity interests you?",
          "How can you become known for something?"
        ],
        'default': [
          "What move would create the most impact?",
          "How can you stand out from peers?",
          "What opportunity are you missing?",
          "Which action accelerates growth?"
        ]
      },
      'okrs': {
        'objective': [
          "What's your main career goal this quarter?",
          "Where do you want to level up?",
          "What achievement would you celebrate?",
          "Which area needs your focus?"
        ],
        'key-result': [
          "How will you measure success?",
          "What number would prove progress?",
          "Which metric matters most?",
          "What milestone shows you're winning?"
        ],
        'default': [
          "What do you want to achieve?",
          "How will you track progress?",
          "Which outcome matters most?",
          "What's your main focus?"
        ]
      },
      'opportunity': {
        'assessment': [
          "What excites you about this opportunity?",
          "What concerns do you have?",
          "How does this fit your goals?",
          "What's the growth potential?"
        ],
        'default': [
          "What opportunity are you evaluating?",
          "How does this align with your vision?",
          "What are the pros and cons?",
          "What's your gut feeling?"
        ]
      }
    };

    const artifactPrompts = prompts[artifactType];
    if (!artifactPrompts) {
      return ["What's most important here?", "How does this help your career?"];
    }

    const sectionPrompts = (artifactPrompts as any)[sectionType] || (artifactPrompts as any)['default'];
    return sectionPrompts || ["What's most important here?", "How does this help your career?"];
  };

  const prompts = getPrompts();
  const icons = [MessageCircle, Clock, Target, Users];

  return (
    <div className="space-y-3">
      <div className="text-sm text-muted-foreground">Quick starters:</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {prompts.map((prompt, index) => {
          const Icon = icons[index % icons.length];
          return (
            <Button
              key={index}
              variant="outline"
              size="sm"
              onClick={() => onPromptSelect(prompt)}
              className="justify-start text-left h-auto p-3 hover:bg-accent/50"
            >
              <Icon className="h-4 w-4 mr-2 text-muted-foreground" />
              <span className="text-sm">{prompt}</span>
            </Button>
          );
        })}
      </div>
    </div>
  );
};